import { Component, EventEmitter, Input, Output, SimpleChanges, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { AlertInfo } from '../../../../models/alert-info';
import { SavedDataInfo } from '../../../../models/form/saved-data-info';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { FormService } from '../../../../services/form.service';
import { RequestsService } from '../../../../services/api/requests.service';

import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { SelectFieldComponent } from '../../../../components/form/select-field/select-field.component';
import { SwitchFieldComponent } from '../../../../components/form/switch-field/switch-field.component';
import { CheckboxFieldComponent } from '../../../../components/form/checkbox-field/checkbox-field.component';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { Subscription } from 'rxjs';
import { CommonService } from 'src/app/services/common.service';

@Component({
  selector: 'requests-date',
  imports: [NgbAlert, InputFieldComponent, SelectFieldComponent, SwitchFieldComponent, CheckboxFieldComponent, ReactiveFormsModule],
  templateUrl: './requests-date.component.html',
  styleUrl: './requests-date.component.scss'
})
export class RequestsDateComponent implements OnDestroy {

  @Input() request?: any;
  @Input() requestCategory?: any;
  @Input() readonlyRequestDate: boolean = false;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  form!: FormGroup;
  selectedCategoryIds: string[] = [];
  alertInfo: AlertInfo = { show: false, type: '', message: '' };
  sourceOptions: { key: string; value: string }[] = [{ key: 'MAIL', value: 'MAIL' }, { key: 'PHONE_CALL', value: 'PHONE_CALL' }, { key: 'SYSTEM', value: 'SYSTEM' }];
  categoriesOptions: { key: string; value: string }[] = [];
  categoryError: string = '';
  currentUser: any = null;
  private userSubscription?: Subscription;

  constructor(
    private formService: FormService,
    private alertNotificationService: AlertNotificationService,
    private requestsService: RequestsService,
    private mainLayoutService: MainLayoutService,
    private commonService: CommonService
  ) {
  }

  ngOnInit(): void {
    this.form = new FormGroup({
      code: new FormControl({ value: '', disabled: true } ),
      requestDate: new FormControl({ value: '', disabled: true }),
      requestTechnicalCall: new FormControl(false, Validators.required),
      taking: new FormControl(''),
      takingOver: new FormControl({ value: '', disabled: true},),
      reminder: new FormControl(false,),
      reminderDate: new FormControl(''),
      source: new FormControl('', Validators.required),
      requestCategoryIds: new FormControl([]),
    });

    this.requestsService.listSettingsRequestCategories().subscribe((res: any) => {
      this.categoriesOptions = res.data.requestCategories?.map((category: any) => ({
        key: category.requestCategoryId,
        value: category.name
      }));

    });

    this.userSubscription = this.mainLayoutService.user$.subscribe(userData => {
      if (userData) {
        console.log('User data received:', userData);
        try {
          this.currentUser = typeof userData === 'string' ? JSON.parse(userData) : userData;
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
    });

    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        this.currentUser = JSON.parse(userData);
        console.log('Current user initialized:', this.currentUser);
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['request'] && changes['request'].currentValue) {
      const requestTimeDate = this.request?.requestTime ? new Date(this.request.requestTime) : null;
      const reminderTimeDate = this.request?.reminderTime ? new Date(this.request.reminderTime) : null;

      this.form.patchValue({
        code: this.request.code,
        requestDate: requestTimeDate && !Number.isNaN(requestTimeDate.getTime())
          ? this.commonService.formatDateTimeLocal(requestTimeDate)
          : '',
        requestTechnicalCall: this.request.requestTechnicalCall,
        taking: this.request.takingCareUser ? true : false,
        takingOver: this.request.takingCareUser ? this.request.takingCareUser.firstName + ' ' + this.request.takingCareUser.lastName : '',
        reminder: this.request.reminder,
        reminderDate: reminderTimeDate && !Number.isNaN(reminderTimeDate.getTime())
          ? this.commonService.formatDateTimeLocal(reminderTimeDate)
          : '',
        source: this.request.source,
      });

      const categories = changes['request'].currentValue;
      if (categories) {
        this.selectedCategoryIds = categories.requestCategories.map((cat: any) => cat.requestCategoryId);
        this.form.patchValue({
          requestCategoryIds: this.selectedCategoryIds,
        });
      }
    }
  }
  getWaitingTimeClass(): string {
    return this.getRequestStatusInput().value ? 'bg-success' : 'bg-danger';
  }

  /**
   * *******************
   * Submit form methods
   * *******************
   */
  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(validationErrors: any) {
    this.formService.resetErrors(this.form);
    this.formService.setValidationErrors(this.form, validationErrors);
  }
  submitFormSuccess(response: any) {
    this.alertNotificationService.success('Operation completed successfully')
    this.showAlertInfo('success', 'Operation completed successfully');

    this.onSavedData.emit({
      success: true,
      response
    });
  }
  submitFormError(error: any) {

    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    }
    else if (error.error.error.description == 'USER_EMAIL_ALREADY_EXISTS') {
      this.updateValidationErrors([{ field: 'email', defaultMessage: error.error.error.message }]);
      this.showAlertInfo('danger', `Please fix the error: ${error.error.error.message}`);
    }
    else {
      this.showAlertInfo('danger', error.error.error.message);
    }

    this.onSavedData.emit({
      success: false,
      error
    });

  }
  validateCategories(): boolean {
    const categoryControl = this.getRequestCategoryInput();
    const categoryValue = categoryControl.value;

    if (!categoryValue || (Array.isArray(categoryValue) && categoryValue.length === 0)) {
      this.categoryError = 'At least one category must be selected';
      categoryControl.setErrors({ 'required': true });
      categoryControl.markAsTouched();
      this.showAlertInfo('danger', 'You must select at least one category');
      return false;
    }

    this.categoryError = '';
    categoryControl.setErrors(null);
    return true;
  }
  submitForm(): void {

    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });

    const categoriesValid = this.validateCategories();

    if (this.form.valid && categoriesValid) {
      const selectedCategoryIds = this.form.value.requestCategoryIds;


      if (selectedCategoryIds.length > 0) {
        const categoryIdsToSubmit = this.categoriesOptions
          .filter(option => selectedCategoryIds.includes(option.value))
          .map(option => option.key);
        this.form.patchValue({
          requestCategoryIds: categoryIdsToSubmit
        });
      }
      
      const valuesBase = {
        tenantId: 1,
      };
      
      console.log(this.getRequestStatusInput().value);
      
      if (this.form.get('taking')?.value === true ) {
        Object.assign(valuesBase, { takingCareUserId: this.currentUser?.userId });
      }
      
      const formValues = { ...this.form.value };
      if (formValues.reminderDate) {
        const reminderDate = new Date(formValues.reminderDate);
        formValues.reminderDate = Number.isNaN(reminderDate.getTime())
          ? ''
          : reminderDate.toISOString();
      }

      const values = Object.assign(
        formValues,
        valuesBase
      );

      this.alertInfo.show = false;
      this.formService.resetErrors(this.form);

      const serviceMethod = this.request ? this.requestsService.update(this.request.requestId, values) : this.requestsService.create(values);
      serviceMethod.subscribe({
        next: (response: any) => {
          this.submitFormSuccess(response)
        },
        error: (error: any) => {
          this.submitFormError(error)
        }
      })
    } else if (!categoriesValid) {
      this.showAlertInfo('danger', 'Select at least one category.');
    }
  }
  /**
   * *******************
   * Submit form methods
   * *******************
   */



  getRequestCodeInput(): FormControl { return this.getFormControl('code'); }
  getRequestRequestDateInput(): FormControl { return this.getFormControl('requestDate'); }
  getRequestRequestInput(): FormControl { return this.getFormControl('requestTechnicalCall'); }
  getRequestStatusInput(): FormControl { return this.getFormControl('taking'); }
  getRequestTakingOverInput(): FormControl { return this.getFormControl('takingOver'); }
  getRequestReminderInput(): FormControl { return this.getFormControl('reminder'); }
  getRequestReminderDateInput(): FormControl { return this.getFormControl('reminderDate'); }
  getRequestSourceSelect(): FormControl { return this.getFormControl('source'); }
  getRequestCategoryInput(): FormControl { return this.getFormControl('requestCategoryIds'); }

  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }

  onStatusChange(newValue: any): void {
    this.getRequestStatusInput().setValue(newValue);
    if (this.request?.date) {
      this.request.date.taking = newValue;
    }
    
    if (newValue === true) {
      if (this.currentUser) {
        const userFullName = `${this.currentUser.firstName || ''} ${this.currentUser.lastName || ''}`.trim();
        console.log('Setting takingOver to:', userFullName);
        this.getRequestTakingOverInput().setValue(userFullName);
        this.form.markAsDirty();
        this.form.updateValueAndValidity();
      } else {
        console.warn('Current user is not available');
        this.mainLayoutService.initializeUserFromLocalStorage();
      }
    } else {
      this.getRequestTakingOverInput().setValue('');
    }
  }
  onCategoriesChange(selectedValues: any): void {
    this.requestCategory = selectedValues;
    this.form.get('requestCategoryIds')?.setValue(selectedValues);

    if (selectedValues && selectedValues.length > 0) {
      this.categoryError = '';
      this.getRequestCategoryInput().setErrors(null);
    } else {
      this.categoryError = 'You must select at least one category';
      this.getRequestCategoryInput().setErrors({ 'manual': this.categoryError });
    }
  }

  onSwitchChange(event: boolean | { isChecked: boolean; item: any }): void {
    if (typeof event === 'boolean') {
      this.form.get('approved')?.setValue(event);
    } else {
      const { isChecked } = event;
      this.form.get('approved')?.setValue(isChecked);
    }
  }
}
